import { TossAuth, type AppLoginResponse } from '@apps-in-toss/web-framework';
import { apiUrl } from '../api-origin';
import { beginRightsSession, currentRightsSessionEpoch, resetRights } from '../../domain/entitlement-snapshot';
import { purchaseDeadline } from './deadline';

export type TossLoginRuntime = { login(): Promise<AppLoginResponse> };
type SessionResponse = { ok?: unknown; data?: { token?: unknown; expiresAt?: unknown; accountTag?: unknown } };

/**
 * One tab's opaque IAP token. This is intentionally module-memory only: it is
 * not a cookie, localStorage value, export field, or analytics property.
 */
export interface TossIapSessionOwner {
  login(): Promise<boolean>;
  logout(): Promise<void>;
  accessToken(): Promise<string | null>;
  epoch(): number;
  cancelLogin?(): void;
  logoutState?(): 'confirmed' | 'unknown';
  retryLogout?(): Promise<boolean>;
}

export function createTossIapSessionOwner(options: { auth?: TossLoginRuntime; fetchImpl?: typeof fetch; timeoutMs?: number } = {}): TossIapSessionOwner {
  const auth = options.auth ?? TossAuth;
  const fetchImpl = options.fetchImpl ?? fetch;
  let token: string | null = null;
  let expiresAt = 0;
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;
  let currentEpoch = currentRightsSessionEpoch();
  let active: AbortController | undefined;
  let loginFlight: Promise<boolean> | undefined;
  let exchangeStarted = false;
  let unknownUntil = 0;
  const pendingLogouts = new Set<string>();
  const timeout = options.timeoutMs ?? 12_000;

  const clear = () => {
    if (exchangeStarted && !token) unknownUntil = Date.now() + 86_400_000;
    active?.abort(); active = undefined; loginFlight = undefined;
    exchangeStarted = false;
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = undefined;
    token = null;
    expiresAt = 0;
    currentEpoch = beginRightsSession();
  };
  const expireAt = (epoch: number, at: number) => {
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = setTimeout(() => {
      if (currentEpoch === epoch && currentRightsSessionEpoch() === epoch && expiresAt === at) clear();
    }, Math.max(0, at - Date.now()));
  };
  const exchange = async (login: AppLoginResponse, signal: AbortSignal) => {
    if (signal.aborted) throw new Error('PURCHASE_CANCELLED');
    exchangeStarted = true;
    const response = await fetchImpl(apiUrl('/api/iap/session/login'), {
      method: 'POST', credentials: 'omit', redirect: 'error', signal, headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ authorizationCode: login.authorizationCode, referrer: login.referrer }),
    });
    const payload = await response.json().catch(() => null) as SessionResponse | null;
    const candidate = payload?.ok === true ? payload.data : null;
    const parsedExpiry = candidate && typeof candidate.expiresAt === 'string' ? Date.parse(candidate.expiresAt) : Number.NaN;
    if (!response.ok || !candidate || typeof candidate.token !== 'string' || !/^[a-f0-9]{64}$/.test(candidate.token) || !Number.isFinite(parsedExpiry) || parsedExpiry <= Date.now() || parsedExpiry > Date.now() + 86_400_000 || typeof candidate.accountTag !== 'string' || !/^[a-f0-9]{64}$/.test(candidate.accountTag)) return null;
    return { token: candidate.token, expiresAt: parsedExpiry, accountTag: candidate.accountTag };
  };

  const retryLogout = async () => {
    // Captured account A credentials never mutate the current account B epoch/token.
    await Promise.all([...pendingLogouts].map(async (prior) => {
      try {
        const confirmed = await purchaseDeadline(async (signal) => {
          const response = await fetchImpl(apiUrl('/api/iap/session/logout'), { method: 'POST', credentials: 'omit', redirect: 'error', signal, headers: { authorization: `Bearer ${prior}` } });
          const payload: unknown = await response.json();
          return response.ok && typeof payload === 'object' && payload !== null && 'ok' in payload && payload.ok === true;
        }, timeout);
        if (confirmed) pendingLogouts.delete(prior);
      } catch { /* Retain only the captured credential in memory for explicit retry. */ }
    }));
    return pendingLogouts.size === 0 && unknownUntil <= Date.now();
  };

  return {
    login() {
      if (loginFlight) return loginFlight;
      if (token) pendingLogouts.add(token);
      // Invalidate every in-flight read before accepting another account.
      const attemptEpoch = beginRightsSession();
      currentEpoch = attemptEpoch;
      token = null;
      const controller = new AbortController(); active = controller;
      const flight = (async () => { try {
        const next = await purchaseDeadline(async (signal) => {
          const login = await auth.login();
          if (signal.aborted || currentEpoch !== attemptEpoch) throw new Error('PURCHASE_CANCELLED');
          return exchange(login, signal);
        }, timeout, controller.signal);
        if (!next || currentEpoch !== attemptEpoch || currentRightsSessionEpoch() !== attemptEpoch) {
          if (currentEpoch === attemptEpoch && exchangeStarted) { unknownUntil = Date.now() + 86_400_000; exchangeStarted = false; }
          return false;
        }
        exchangeStarted = false;
        token = next.token;
        expiresAt = next.expiresAt;
        expireAt(attemptEpoch, next.expiresAt);
        return true;
      } catch {
        if (currentEpoch === attemptEpoch) {
          if (exchangeStarted) unknownUntil = Date.now() + 86_400_000;
          exchangeStarted = false;
          resetRights(attemptEpoch);
        }
        return false;
      } finally { if (active === controller) active = undefined; } })();
      loginFlight = flight;
      void flight.finally(() => { if (loginFlight === flight) loginFlight = undefined; });
      return flight;
    },
    async logout() {
      const prior = token;
      clear();
      if (prior) pendingLogouts.add(prior);
      // The local epoch is invalidated before the network request. A delayed
      // response therefore cannot revive old-account rights.
      await retryLogout();
    },
    async accessToken() {
      if (!token) return null;
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now() || currentRightsSessionEpoch() !== currentEpoch) {
        clear();
        return null;
      }
      return token;
    },
    epoch() { return currentEpoch; },
    // Leaving a completed Premium view must preserve verified rights for packing/trip consumers.
    cancelLogin() { if (active) clear(); },
    logoutState() { return pendingLogouts.size || unknownUntil > Date.now() ? 'unknown' : 'confirmed'; },
    retryLogout,
  };
}
